import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useRolePermissions } from '@/hooks/useRolePermissions';
import { toast } from '@/components/ui/use-toast';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, Search, RefreshCw, AlertCircle } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import UserMigration from '@/components/admin/user-management/UserMigration';
import { useAuth } from '@/contexts/AuthContext';

interface User {
  id: string;
  email: string;
  role: string;
  is_active: boolean;
  subscription_status: string;
  plan_tier: string;
  created_at: string;
}

const UsersAdmin: React.FC = () => {
  const { isSuper, loading: permissionsLoading } = useRolePermissions();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  
  const [activeTab, setActiveTab] = useState(tabParam === 'migration' ? 'migration' : 'users');

  const fetchUsers = async () => {
    setLoading(true);
    try {
      // Check if we have service role access first
      let hasServiceAccess = false;
      try {
        const { data: testData, error: testError } = await supabase.auth.admin.listUsers({ page: 1, perPage: 1 });
        if (!testError && testData) {
          hasServiceAccess = true;
        }
      } catch (error) {
        console.log("No service role access, will use fallback data");
      }

      if (hasServiceAccess) {
        // Get users from Auth service first
        const { data: authUsers, error: authError } = await supabase.auth.admin.listUsers();
        
        if (authError) throw authError;

        // Get user roles from the user_roles table
        const { data: rolesData, error: rolesError } = await supabase
          .from('user_roles')
          .select('user_id, role, is_active, subscription_status, plan_tier');

        if (rolesError) throw rolesError;

        // Map roles to the users
        const rolesMap = new Map();
        rolesData?.forEach(role => {
          rolesMap.set(role.user_id, {
            role: role.role,
            is_active: role.is_active,
            subscription_status: role.subscription_status,
            plan_tier: role.plan_tier
          });
        });

        // Combine auth users with their roles
        const combinedUsers = authUsers?.users.map(user => {
          const roleInfo = rolesMap.get(user.id) || {
            role: 'member',
            is_active: true,
            subscription_status: 'free',
            plan_tier: 'basic'
          };

          return {
            id: user.id,
            email: user.email,
            role: roleInfo.role,
            is_active: roleInfo.is_active,
            subscription_status: roleInfo.subscription_status,
            plan_tier: roleInfo.plan_tier,
            created_at: new Date(user.created_at).toLocaleDateString()
          };
        });

        setUsers(combinedUsers || []);
        setFilteredUsers(combinedUsers || []);
      } else {
        // Try to get both users and roles directly from user_roles
        const { data: rolesData, error: rolesError } = await supabase
          .from('user_roles')
          .select('*');

        if (rolesError) {
          throw new Error("Couldn't fetch user roles data");
        }

        const simplifiedUsers = rolesData?.map(role => ({
          id: role.user_id,
          email: `user-${role.user_id.substring(0, 8)}@example.com`, // Placeholder email
          role: role.role,
          is_active: role.is_active,
          subscription_status: role.subscription_status || 'free',
          plan_tier: role.plan_tier || 'basic',
          created_at: new Date(role.created_at).toLocaleDateString()
        })) || [];

        setUsers(simplifiedUsers);
        setFilteredUsers(simplifiedUsers);

        if (simplifiedUsers.length === 0) {
          // Create demo data for testing
          const demoUsers = createDemoUsers();
          setUsers(demoUsers);
          setFilteredUsers(demoUsers);

          toast({
            title: "מצב דמה בלבד",
            description: "אין הרשאות שירות. מוצגים משתמשי דוגמה.",
            variant: "destructive"
          });
        }
      }
    } catch (error: any) {
      console.error('Error fetching users:', error);
      toast({
        variant: "destructive",
        title: "שגיאה בטעינת משתמשים",
        description: error.message || "אירעה שגיאה בטעינת רשימת המשתמשים",
      });
      
      // For development demo purposes, create dummy data
      const dummyUsers = createDemoUsers();
      setUsers(dummyUsers);
      setFilteredUsers(dummyUsers);
    } finally {
      setLoading(false);
    }
  };

  const createDemoUsers = () => {
    return Array(5).fill(0).map((_, i) => ({
      id: `dummy-${i}`,
      email: `user${i}@example.com`,
      role: i === 0 ? 'super' : i === 1 ? 'admin' : 'member',
      is_active: i !== 4,
      subscription_status: i === 3 ? 'premium' : 'free',
      plan_tier: i === 3 ? 'premium' : 'basic',
      created_at: new Date().toLocaleDateString()
    }));
  };

  useEffect(() => {
    // Check if user is super, if not redirect to unauthorized
    if (!permissionsLoading && !isSuper) {
      toast({
        variant: "destructive",
        title: "גישה נדחתה",
        description: "אין לך הרשאות גישה לדף זה, נדרשת הרשאת מנהל-על",
      });
      navigate('/unauthorized');
      return;
    }
    
    if (!permissionsLoading && isSuper) {
      fetchUsers();
    }
  }, [isSuper, permissionsLoading, navigate]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredUsers(users);
    } else {
      const lowercaseQuery = searchQuery.toLowerCase();
      setFilteredUsers(
        users.filter(
          user => 
            user.email.toLowerCase().includes(lowercaseQuery) ||
            user.role.toLowerCase().includes(lowercaseQuery) ||
            user.plan_tier.toLowerCase().includes(lowercaseQuery) ||
            user.subscription_status.toLowerCase().includes(lowercaseQuery)
        )
      );
    }
  }, [searchQuery, users]);

  useEffect(() => {
    // Update the tab when URL parameters change
    if (tabParam === 'migration') {
      setActiveTab('migration');
    } else {
      setActiveTab('users');
    }
  }, [tabParam]);

  const updateUserRole = async (userId: string, role: string) => {
    try {
      const { error } = await supabase
        .from('user_roles')
        .update({ role })
        .eq('user_id', userId);

      if (error) throw error;

      // Update the local state
      setUsers(users.map(user => 
        user.id === userId ? { ...user, role } : user
      ));
      setFilteredUsers(filteredUsers.map(user => 
        user.id === userId ? { ...user, role } : user
      ));

      toast({
        title: "תפקיד עודכן",
        description: `תפקיד המשתמש עודכן בהצלחה ל-${role}`,
      });
    } catch (error: any) {
      console.error('Error updating user role:', error);
      toast({
        variant: "destructive",
        title: "שגיאה בעדכון תפקיד",
        description: error.message || "אירעה שגיאה בעדכון תפקיד המשתמש",
      });
    }
  };

  const updateUserActiveStatus = async (userId: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from('user_roles')
        .update({ is_active: isActive })
        .eq('user_id', userId);

      if (error) throw error;

      // Update the local state
      setUsers(users.map(user => 
        user.id === userId ? { ...user, is_active: isActive } : user
      ));
      setFilteredUsers(filteredUsers.map(user => 
        user.id === userId ? { ...user, is_active: isActive } : user
      ));

      toast({
        title: "סטטוס משתמש עודכן",
        description: `המשתמש ${isActive ? 'הופעל' : 'הושבת'} בהצלחה`,
      });
    } catch (error: any) {
      console.error('Error updating user active status:', error);
      toast({
        variant: "destructive",
        title: "שגיאה בעדכון סטטוס",
        description: error.message || "אירעה שגיאה בעדכון סטטוס המשתמש",
      });
    }
  };

  if (permissionsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-synagogue-blue" />
      </div>
    );
  }

  // Show access denied if not super admin
  if (!isSuper) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>גישה נדחתה</AlertTitle>
          <AlertDescription>
            רק משתמשים בעלי הרשאות מנהל-על יכולים לגשת לדף זה.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <h1 className="text-2xl font-bold">ניהול משתמשים (מנהל-על)</h1>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="users">משתמשים</TabsTrigger>
            <TabsTrigger value="migration">מיגרציה וניהול הרשאות</TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="mt-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="relative">
                <Search className="h-4 w-4 absolute top-3 left-3 text-gray-400" />
                <Input
                  placeholder="חיפוש משתמשים..."
                  className="pl-10 w-full md:w-[300px]"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              <Button variant="outline" onClick={fetchUsers} title="רענן רשימת משתמשים">
                <RefreshCw className="h-4 w-4" />
                <span className="sr-only">רענן</span>
              </Button>
            </div>
            
            {loading ? (
              <div className="h-96 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-synagogue-blue" />
                <span className="mr-2">טוען משתמשים...</span>
              </div>
            ) : (
              <div className="border rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>משתמש</TableHead>
                      <TableHead>תפקיד</TableHead>
                      <TableHead>תוכנית</TableHead>
                      <TableHead>סטטוס מנוי</TableHead>
                      <TableHead>פעיל</TableHead>
                      <TableHead>תאריך יצירה</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8">
                          לא נמצאו משתמשים
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredUsers.map(user => (
                        <TableRow key={user.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Avatar className="h-8 w-8">
                                <AvatarFallback className="bg-synagogue-blue text-white text-xs">
                                  {user.email.substring(0, 2).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div className="font-medium">{user.email}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Select 
                              defaultValue={user.role}
                              onValueChange={(value) => updateUserRole(user.id, value)}
                            >
                              <SelectTrigger className="w-[130px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="super">מנהל מערכת עליון</SelectItem>
                                <SelectItem value="admin">מנהל</SelectItem>
                                <SelectItem value="member">משתמש רגיל</SelectItem>
                                <SelectItem value="viewer">צפייה בלבד</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Select 
                              defaultValue={user.plan_tier}
                              onValueChange={async (value) => {
                                try {
                                  await supabase
                                    .from('user_roles')
                                    .update({ plan_tier: value })
                                    .eq('user_id', user.id);
                                  
                                  setUsers(users.map(u => 
                                    u.id === user.id ? { ...u, plan_tier: value } : u
                                  ));
                                  setFilteredUsers(filteredUsers.map(u => 
                                    u.id === user.id ? { ...u, plan_tier: value } : u
                                  ));
                                  
                                  toast({
                                    title: "תוכנית עודכנה",
                                    description: `תוכנית המשתמש עודכנה בהצלחה`,
                                  });
                                } catch (error) {
                                  console.error('Error updating plan:', error);
                                  toast({
                                    variant: "destructive",
                                    title: "שגיאה בעדכון תוכנית",
                                    description: "אירעה שגיאה בעת עדכון תוכנית המשתמש",
                                  });
                                }
                              }}
                            >
                              <SelectTrigger className="w-[130px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="basic">בסיסי</SelectItem>
                                <SelectItem value="premium">פרימיום</SelectItem>
                                <SelectItem value="professional">מקצועי</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Select 
                              defaultValue={user.subscription_status}
                              onValueChange={async (value) => {
                                try {
                                  await supabase
                                    .from('user_roles')
                                    .update({ subscription_status: value })
                                    .eq('user_id', user.id);
                                  
                                  setUsers(users.map(u => 
                                    u.id === user.id ? { ...u, subscription_status: value } : u
                                  ));
                                  setFilteredUsers(filteredUsers.map(u => 
                                    u.id === user.id ? { ...u, subscription_status: value } : u
                                  ));
                                  
                                  toast({
                                    title: "סטטוס מנוי עודכן",
                                    description: `סטטוס המנוי עודכן בהצלחה`,
                                  });
                                } catch (error) {
                                  console.error('Error updating subscription:', error);
                                  toast({
                                    variant: "destructive",
                                    title: "שגיאה בעדכון מנוי",
                                    description: "אירעה שגיאה בעת עדכון סטטוס המנוי",
                                  });
                                }
                              }}
                            >
                              <SelectTrigger className="w-[130px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="free">חינמי</SelectItem>
                                <SelectItem value="active">פעיל</SelectItem>
                                <SelectItem value="trial">ניסיון</SelectItem>
                                <SelectItem value="expired">פג תוקף</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Switch
                              checked={user.is_active}
                              onCheckedChange={(checked) => updateUserActiveStatus(user.id, checked)}
                            />
                          </TableCell>
                          <TableCell>{user.created_at}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="migration" className="mt-6">
            <UserMigration />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default UsersAdmin;
